<template>
  <v-container class="mt-4 ml-2" fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="shadowCard">
          <v-card-title class="flex-wrap">
            <span class="text-subtitle-1">Solicitud de comprobante de pago</span>
            <v-chip v-if="esRevisor" small outlined color="blue-grey" class="ml-2">
              <v-icon left x-small>mdi-shield-check-outline</v-icon>
              Revisor
            </v-chip>
            <v-spacer />
            <v-btn
              class="mr-2"
              small
              dark
              color="green"
              tile
              @click="exportarTabla"
            >
              <v-icon small>mdi-microsoft-excel</v-icon>
            </v-btn>
            <v-btn
              class="mr-2"
              color="blue-grey darken-1"
              dark
              small
              tile
              @click="abrirDialogNueva"
            >
              <v-icon left small>mdi-upload</v-icon>
              Nueva solicitud
            </v-btn>
            <v-btn color="primary" small tile @click="cargarSolicitudes">
              <v-icon left small>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-sheet class="pa-3 rounded-lg mb-4" color="grey lighten-4" elevation="0">
              <div class="text-overline grey--text text--darken-1 mb-2">Resumen</div>
              <div class="d-flex flex-wrap align-center" style="gap: 8px;">
                <v-chip
                  small
                  label
                  :outlined="filtroEstatus !== 'todos'"
                  :color="filtroEstatus === 'todos' ? 'blue-grey darken-1' : 'grey lighten-2'"
                  :text-color="filtroEstatus === 'todos' ? 'white' : 'blue-grey darken-2'"
                  class="font-weight-medium"
                  @click="filtroEstatus = 'todos'"
                >
                  <v-icon left x-small>mdi-file-document-multiple-outline</v-icon>
                  Todas ({{ kpi.todos }})
                </v-chip>
                <v-chip
                  small
                  label
                  :outlined="filtroEstatus !== 'pendiente'"
                  :color="filtroEstatus === 'pendiente' ? 'blue-grey darken-1' : 'grey lighten-2'"
                  :text-color="filtroEstatus === 'pendiente' ? 'white' : 'blue-grey darken-2'"
                  class="font-weight-medium"
                  @click="filtroEstatus = 'pendiente'"
                >
                  <v-icon left x-small>mdi-clock-outline</v-icon>
                  Pendientes ({{ kpi.pendientes }})
                </v-chip>
                <v-chip
                  small
                  label
                  :outlined="filtroEstatus !== 'aceptado'"
                  :color="filtroEstatus === 'aceptado' ? 'blue-grey darken-1' : 'grey lighten-2'"
                  :text-color="filtroEstatus === 'aceptado' ? 'white' : 'blue-grey darken-2'"
                  class="font-weight-medium"
                  @click="filtroEstatus = 'aceptado'"
                >
                  <v-icon left x-small>mdi-check-circle-outline</v-icon>
                  Aceptadas ({{ kpi.aceptadas }})
                </v-chip>
                <v-chip
                  small
                  label
                  :outlined="filtroEstatus !== 'rechazado'"
                  :color="filtroEstatus === 'rechazado' ? 'blue-grey darken-1' : 'grey lighten-2'"
                  :text-color="filtroEstatus === 'rechazado' ? 'white' : 'blue-grey darken-2'"
                  class="font-weight-medium"
                  @click="filtroEstatus = 'rechazado'"
                >
                  <v-icon left x-small>mdi-close-circle-outline</v-icon>
                  Rechazadas ({{ kpi.rechazadas }})
                </v-chip>
              </div>
            </v-sheet>

            <v-row dense class="mb-2">
              <v-col cols="12" sm="6" md="4">
                <v-autocomplete
                  v-model="filtroCiclo"
                  :items="ciclos"
                  item-text="ciclo"
                  item-value="id_ciclo"
                  label="Ciclo"
                  dense
                  outlined
                  hide-details
                  clearable
                  color="blue-grey"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="search"
                  label="Buscar"
                  dense
                  outlined
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  color="blue-grey"
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="filasTabla"
              :search="search"
              dense
              class="elevation-0"
              :loading="cargar"
              loading-text="Cargando…"
              :mobile-breakpoint="0"
            >
              <template v-slot:item.estatus="{ item }">
                <v-chip
                  small
                  outlined
                  :color="colorEstatusChip(item.estatus)"
                  :text-color="textoEstatusColor(item.estatus)"
                >
                  {{ etiquetaEstatus(item.estatus) }}
                </v-chip>
              </template>

              <template v-slot:item.solicito="{ item }">
                <span class="text-body-2">Solicitó: {{ item.solicito != null && item.solicito !== '' ? item.solicito : '—' }}</span>
              </template>

              <template v-slot:item.comprobante_thumb="{ item }">
                <div
                  class="d-flex align-center"
                  style="cursor: pointer; max-width: 88px;"
                  title="Ver comprobante"
                  @click="verDetalle(item)"
                >
                  <v-sheet
                    height="44"
                    width="56"
                    class="grey lighten-3 rounded d-flex align-center justify-center"
                  >
                    <v-icon :color="esPdfItem(item) ? 'red darken-2' : 'blue-grey'">
                      {{ esPdfItem(item) ? 'mdi-file-pdf-box' : 'mdi-image-outline' }}
                    </v-icon>
                  </v-sheet>
                </div>
              </template>

              <template v-slot:item.texto_corto="{ item }">
                <span class="text-caption grey--text text--darken-2">{{ item.texto_corto }}</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  v-if="esRevisor && normalizarEstatus(item.estatus) === 0"
                  x-small
                  outlined
                  color="success"
                  class="mr-1 font-weight-medium"
                  @click="aceptar(item)"
                >
                  Aceptar
                </v-btn>
                <v-btn
                  v-if="esRevisor && normalizarEstatus(item.estatus) === 0"
                  x-small
                  outlined
                  color="error"
                  class="mr-1 font-weight-medium"
                  @click="abrirRechazo(item)"
                >
                  Rechazar
                </v-btn>
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  class="font-weight-medium"
                  @click="verDetalle(item)"
                >
                  <v-icon x-small left>mdi-eye</v-icon>
                  Ver
                </v-btn>
              </template>

              <template v-slot:no-data>
                <div class="grey--text py-4 text-center">
                  No hay solicitudes. Crea una nueva o verifica la conexión con el servidor.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Nueva solicitud: subir → OCR → datos -->
    <v-dialog v-model="dialogNueva" max-width="720" persistent scrollable>
      <v-card>
        <v-card-title class="subtitle-1">
          Nueva solicitud
          <v-spacer />
          <v-btn icon small @click="cerrarDialogNueva"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-caption grey--text mb-6">
            Sube una imagen o PDF del comprobante. Se enviará a lectura OCR y podrás completar los datos.
          </p>

          <div class="mb-6">
            <v-file-input
              v-model="archivoComprobante"
              label="Comprobante"
              prepend-icon="mdi-paperclip"
              outlined
              dense
              accept="image/*,.pdf,application/pdf"
              :disabled="procesandoOcr"
              hide-details
              @change="onArchivoElegido"
            />
          </div>

          <v-progress-linear v-if="procesandoOcr" indeterminate color="blue-grey" class="mb-6" />

          <template v-if="ocrResultado && ocrResultado.ok">
            <v-alert dense outlined type="info" color="blue-grey" class="mb-6">
              Lectura lista. El texto detectado es solo lectura; completa ciclo, alumno y grupo.
            </v-alert>

            <div class="mb-6">
              <v-textarea
                :value="ocrResultado.textoNormalizado"
                label="Texto normalizado (solo lectura)"
                outlined
                dense
                rows="4"
                hide-details
                readonly
                filled
                background-color="grey lighten-4"
                color="blue-grey"
              />
            </div>

            <div class="mb-6">
              <v-text-field
                v-model="formSolicitud.fecha"
                label="Fecha del movimiento"
                type="date"
                outlined
                dense
                hide-details
                color="blue-grey"
              />
            </div>

            <div class="mb-6">
              <v-autocomplete
                v-model="formSolicitud.id_ciclo"
                :items="ciclos"
                item-text="ciclo"
                item-value="id_ciclo"
                label="Ciclo"
                outlined
                dense
                hide-details
                clearable
                color="blue-grey"
                hint="Primero elige el ciclo para cargar los grupos activos"
                persistent-hint
              />
            </div>

            <div class="mb-6">
              <v-autocomplete
                v-model="formSolicitud.id_alumno"
                :items="alumnosEscuela"
                :item-text="textoItemAlumno"
                item-value="id_alumno"
                label="Alumno"
                outlined
                dense
                hide-details
                clearable
                :loading="cargandoAlumnos"
                no-data-text="Sin alumnos (actualiza o revisa escuela)"
                color="blue-grey"
                hint="Listado según tu escuela"
                persistent-hint
              />
            </div>

            <div class="mb-6">
              <v-autocomplete
                v-model="formSolicitud.id_grupo"
                :items="gruposCiclo"
                :item-text="textoItemGrupo"
                item-value="id_grupo"
                label="Grupo"
                outlined
                dense
                hide-details
                clearable
                :disabled="!formSolicitud.id_ciclo"
                :loading="cargandoGrupos"
                no-data-text="Selecciona un ciclo o no hay grupos activos"
                color="blue-grey"
                hint="Solo grupos con activo_sn = 1 del ciclo elegido"
                persistent-hint
              />
            </div>

            <div class="mb-2">
              <v-textarea
                v-model="formSolicitud.motivo"
                label="Motivo de la solicitud"
                outlined
                dense
                rows="3"
                hide-details
                color="blue-grey"
              />
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cerrarDialogNueva">Cancelar</v-btn>
          <v-btn
            color="blue-grey darken-1"
            dark
            :disabled="!puedeGuardarSolicitud || guardandoSolicitud"
            :loading="guardandoSolicitud"
            @click="guardarSolicitud"
          >
            Guardar solicitud
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rechazo -->
    <v-dialog v-model="dialogRechazo" max-width="480">
      <v-card>
        <v-card-title class="subtitle-1">Rechazar solicitud</v-card-title>
        <v-card-text>
          <p class="text-caption grey--text">Indica el motivo del rechazo (obligatorio).</p>
          <v-textarea
            v-model="comentarioRechazo"
            outlined
            dense
            rows="3"
            label="Motivo del rechazo"
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogRechazo = false">Cancelar</v-btn>
          <v-btn
            color="brown darken-1"
            dark
            :disabled="!comentarioRechazo || !comentarioRechazo.trim()"
            :loading="procesandoAccion"
            @click="confirmarRechazo"
          >
            Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detalle -->
    <v-dialog v-model="dialogDetalle" max-width="920" scrollable>
      <v-card v-if="detalleItem">
        <v-card-title class="subtitle-1">
          Solicitud #{{ detalleItem.id }}
          <v-spacer />
          <v-btn icon small @click="dialogDetalle = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-subtitle-2 font-weight-medium mb-2">Comprobante (revisar antes de autorizar)</p>
          <p class="text-caption grey--text mb-2">
            El archivo se carga con tu sesión (axios); no se usa la URL del API en <code>&lt;img src&gt;</code>.
          </p>

          <v-progress-linear
            v-if="cargandoComprobanteBlob"
            indeterminate
            color="blue-grey"
            class="mb-4"
          />

          <v-alert
            v-if="errorComprobanteBlob"
            dense
            outlined
            type="error"
            class="mb-4"
          >
            {{ errorComprobanteBlob }}
          </v-alert>

          <v-sheet
            v-else-if="comprobanteBlobUrl"
            class="mb-6 pa-2 rounded grey lighten-4"
            outlined
          >
            <v-img
              v-if="!comprobanteVistaEsPdf"
              :src="comprobanteBlobUrl"
              contain
              max-height="420"
              class="grey lighten-3 rounded"
            />
            <div v-else class="rounded" style="min-height: 420px;">
              <embed
                :src="comprobanteBlobUrl"
                type="application/pdf"
                width="100%"
                height="420px"
                class="rounded"
              />
            </div>
            <div class="d-flex flex-wrap align-center mt-2">
              <span v-if="detalleItem.image_id" class="text-caption grey--text mr-3">ID imagen OCR: {{ detalleItem.image_id }}</span>
              <span v-if="comprobanteVistaEsPdf" class="text-caption grey--text mr-3">PDF</span>
              <v-btn
                x-small
                text
                color="primary"
                @click="abrirComprobanteBlobEnPestana"
              >
                <v-icon left x-small>mdi-open-in-new</v-icon>
                Abrir en otra pestaña
              </v-btn>
            </div>
          </v-sheet>

          <v-simple-table dense>
            <tbody>
              <tr>
                <td class="font-weight-medium">Solicitó</td>
                <td>Solicitó: {{ detalleItem.solicito != null && detalleItem.solicito !== '' ? detalleItem.solicito : '—' }}</td>
              </tr>
              <tr><td class="font-weight-medium">Estatus</td><td>{{ etiquetaEstatus(detalleItem.estatus) }}</td></tr>
              <tr><td class="font-weight-medium">Fecha</td><td>{{ detalleItem.fecha }}</td></tr>
              <tr><td class="font-weight-medium">Ciclo</td><td>{{ detalleItem.ciclo }}</td></tr>
              <tr><td class="font-weight-medium">Alumno</td><td>{{ detalleItem.alumno }}</td></tr>
              <tr><td class="font-weight-medium">Grupo</td><td>{{ detalleItem.grupo }}</td></tr>
              <tr><td class="font-weight-medium">Motivo</td><td>{{ detalleItem.motivo }}</td></tr>
              <tr v-if="detalleItem.comentario_rechazo"><td class="font-weight-medium">Rechazo</td><td>{{ detalleItem.comentario_rechazo }}</td></tr>
            </tbody>
          </v-simple-table>
          <p class="text-caption mt-3 grey--text">Texto normalizado</p>
          <pre class="text-caption pa-2 grey lighten-4 rounded" style="white-space: pre-wrap; max-height: 240px; overflow: auto;">{{ detalleItem.texto_normalizado }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="cargar && !dialogNueva" />
  </v-container>
</template>

<script>
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
import funcionesExcel from '@/mixins/funcionesExcel'
import validarErrores from '@/mixins/validarErrores'
import { mapGetters } from 'vuex'

/** Solo estos iderp pueden aceptar o rechazar solicitudes */
const IDS_REVISORES = [568, 1, 334]

/** Estos iderp deben usar escuela 3 al consultar inscripciones.alumnos.escuela (misma regla que BuscarAlumno + 568) */
const IDS_ESCUELA_TRES_ALUMNOS = [1, 334, 568]

/**
 * Rutas esperadas en API (crear en backend):
 * GET  solicitud.comprobantes
 * POST solicitud.comprobante  multipart: comprobante (archivo), + campos texto_normalizado, id_usuario, fecha, motivo, id_alumno, id_grupo, id_ciclo, image_id, es_pdf
 * GET archivo: solicitud.comprobante.archivo/:id (id solicitud) — blob en front con axios + responseType blob (no usar URL en <img src>).
 * GET listado: incluir es_pdf, image_id, solicito, etc.
 * POST solicitud.comprobante.estatus  body: id, estatus (1=aceptado, 2=rechazado), comentario_rechazo?, id_revisor
 */
export default {
  name: 'SolicitudComprobante',

  components: { Alerta, carga },

  mixins: [validarErrores, funcionesExcel],

  data () {
    return {
      ciclos: [],
      solicitudes: [],

      filtroCiclo: null,
      /** todos | pendiente | aceptado | rechazado */
      filtroEstatus: 'todos',
      search: '',

      dialogNueva: false,
      archivoComprobante: null,
      procesandoOcr: false,
      ocrResultado: null,

      formSolicitud: {
        fecha: '',
        motivo: '',
        id_alumno: null,
        id_grupo: null,
        id_ciclo: null,
      },

      guardandoSolicitud: false,

      dialogRechazo: false,
      itemRechazo: null,
      comentarioRechazo: '',
      procesandoAccion: false,

      dialogDetalle: false,
      detalleItem: null,

      comprobanteBlobUrl: null,
      cargandoComprobanteBlob: false,
      errorComprobanteBlob: null,
      /** Derivado del Blob (mime) y/o es_pdf del ítem */
      comprobanteVistaEsPdf: false,

      alumnosEscuela: [],
      gruposCiclo: [],
      cargandoAlumnos: false,
      cargandoGrupos: false,

      headers: [
        { text: 'ID', value: 'id', width: '72' },
        { text: 'Comprobante', value: 'comprobante_thumb', sortable: false, width: '96' },
        { text: 'Solicitó', value: 'solicito' },
        { text: 'Ciclo', value: 'ciclo' },
        { text: 'Alumno', value: 'alumno' },
        { text: 'Grupo', value: 'grupo' },
        { text: 'Motivo', value: 'motivo' },
        { text: 'Estatus', value: 'estatus' },
        { text: 'Texto', value: 'texto_corto' },
        { text: '', value: 'actions', sortable: false, width: '200' },
      ],
    }
  },

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP', 'getEscuela']),

    idUsuarioActual () {
      const u = this.getdatosUsuarioERPERP
      if (!u) return null
      return Number(u.iderp != null ? u.iderp : u.id_usuario)
    },

    /** Escuela para GET inscripciones.alumnos.escuela/:escuela */
    escuelaParaAlumnos () {
      const uid = Number(this.idUsuarioActual)
      if (IDS_ESCUELA_TRES_ALUMNOS.includes(uid)) return 3
      const e = this.getEscuela
      if (e != null && e !== '') return Number(e)
      return null
    },

    esRevisor () {
      const id = this.idUsuarioActual
      return id != null && IDS_REVISORES.includes(Number(id))
    },

    /** Solicitudes visibles: revisores ven todo; el resto solo las propias */
    solicitudesFiltradasUsuario () {
      const mine = (row) => Number(row.id_usuario) === Number(this.idUsuarioActual)
      if (this.esRevisor) return [...this.solicitudes]
      return this.solicitudes.filter(mine)
    },

    baseKpi () {
      let rows = this.solicitudesFiltradasUsuario
      if (this.filtroCiclo != null && this.filtroCiclo !== '') {
        rows = rows.filter((r) => Number(r.id_ciclo) === Number(this.filtroCiclo))
      }
      return rows
    },

    kpi () {
      const rows = this.baseKpi
      return {
        todos: rows.length,
        pendientes: rows.filter((r) => this.normalizarEstatus(r.estatus) === 0).length,
        aceptadas: rows.filter((r) => this.normalizarEstatus(r.estatus) === 1).length,
        rechazadas: rows.filter((r) => this.normalizarEstatus(r.estatus) === 2).length,
      }
    },

    filasTabla () {
      let rows = [...this.solicitudesFiltradasUsuario]

      if (this.filtroCiclo != null && this.filtroCiclo !== '') {
        rows = rows.filter((r) => Number(r.id_ciclo) === Number(this.filtroCiclo))
      }

      if (this.filtroEstatus === 'pendiente') {
        rows = rows.filter((r) => this.normalizarEstatus(r.estatus) === 0)
      } else if (this.filtroEstatus === 'aceptado') {
        rows = rows.filter((r) => this.normalizarEstatus(r.estatus) === 1)
      } else if (this.filtroEstatus === 'rechazado') {
        rows = rows.filter((r) => this.normalizarEstatus(r.estatus) === 2)
      }

      return rows.map((r) => {
        const t = r.texto_normalizado != null ? String(r.texto_normalizado) : ''
        return {
          ...r,
          texto_corto: t ? (t.slice(0, 80) + (t.length > 80 ? '…' : '')) : '—',
        }
      })
    },

    puedeGuardarSolicitud () {
      const idA = Number(this.formSolicitud.id_alumno)
      const idG = Number(this.formSolicitud.id_grupo)
      return (
        this.archivoComprobante &&
        this.ocrResultado &&
        this.ocrResultado.ok &&
        this.formSolicitud.fecha &&
        String(this.formSolicitud.motivo || '').trim() &&
        idA > 0 &&
        idG > 0 &&
        this.formSolicitud.id_ciclo
      )
    },
  },

  watch: {
    'formSolicitud.id_ciclo' (val) {
      this.formSolicitud.id_grupo = null
      this.gruposCiclo = []
      if (val) this.cargarGruposPorCiclo(val)
    },
    dialogDetalle (val) {
      if (!val) {
        this.revocarComprobanteBlob()
        this.errorComprobanteBlob = null
        this.cargandoComprobanteBlob = false
      }
    },
  },

  created () {
    this.getCiclos()
    this.cargarSolicitudes()
  },

  beforeDestroy () {
    this.revocarComprobanteBlob()
  },

  methods: {
    normalizarEstatus (v) {
      if (v === 'pendiente' || v === 0 || v === '0') return 0
      if (v === 'aceptado' || v === 'aceptada' || v === 1 || v === '1') return 1
      if (v === 'rechazado' || v === 'rechazada' || v === 2 || v === '2') return 2
      return Number(v)
    },

    etiquetaEstatus (v) {
      const n = this.normalizarEstatus(v)
      if (n === 1) return 'Aceptada'
      if (n === 2) return 'Rechazada'
      return 'Pendiente'
    },

    colorEstatusChip (v) {
      const n = this.normalizarEstatus(v)
      if (n === 1) return 'blue-grey lighten-3'
      if (n === 2) return 'grey lighten-2'
      return 'brown lighten-4'
    },

    textoEstatusColor (v) {
      const n = this.normalizarEstatus(v)
      if (n === 1) return 'blue-grey darken-2'
      if (n === 2) return 'grey darken-2'
      return 'brown darken-2'
    },

    getCiclos () {
      return this.$http
        .get('ciclos.activos.erp')
        .then((response) => {
          this.ciclos = response.data || []
        })
        .catch(() => {})
    },

    textoItemAlumno (item) {
      if (!item) return ''
      const m = item.matricula != null ? String(item.matricula) : ''
      const a = item.alumno != null ? String(item.alumno) : ''
      if (m && a) return `${m} — ${a}`
      return a || m || `ID ${item.id_alumno}`
    },

    textoItemGrupo (item) {
      if (!item) return ''
      return item.grupo || item.grupo_viejo || `Grupo ${item.id_grupo}`
    },

    cargarAlumnosEscuela () {
      const esc = this.escuelaParaAlumnos
      if (esc == null || esc === '') {
        this.alumnosEscuela = []
        this.validarErrorDirecto('No se pudo determinar la escuela para listar alumnos.')
        return Promise.resolve()
      }
      this.cargandoAlumnos = true
      return this.$http
        .get('inscripciones.alumnos.escuela/' + esc)
        .then((response) => {
          this.alumnosEscuela = response.data || []
        })
        .catch((error) => {
          this.alumnosEscuela = []
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'Error al cargar alumnos'
          this.validarError(msg)
        })
        .finally(() => {
          this.cargandoAlumnos = false
        })
    },

    cargarGruposPorCiclo (id_ciclo) {
      if (!id_ciclo) {
        this.gruposCiclo = []
        return Promise.resolve()
      }
      this.cargandoGrupos = true
      return this.$http
        .post('grupos.list', { id_ciclo: Number(id_ciclo) })
        .then((response) => {
          const raw = response.data || []
          this.gruposCiclo = raw.filter((g) => Number(g.activo_sn) === 1)
        })
        .catch((error) => {
          this.gruposCiclo = []
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'Error al cargar grupos'
          this.validarError(msg)
        })
        .finally(() => {
          this.cargandoGrupos = false
        })
    },

    cargarSolicitudes () {
      this.cargar = true
      const params = {}
      if (this.esRevisor) params.vista = 'todas'

      return this.$http
        .get('solicitud.comprobantes', { params })
        .then((response) => {
          this.solicitudes = Array.isArray(response.data) ? response.data : response.data.data || []
        })
        .catch((error) => {
          this.solicitudes = []
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'No se pudo cargar el listado. Verifica que la ruta solicitud.comprobantes exista en el API.'
          this.validarError(typeof msg === 'string' ? msg : 'Error al cargar solicitudes')
        })
        .finally(() => {
          this.cargar = false
        })
    },

    abrirDialogNueva () {
      this.resetFormNueva()
      this.dialogNueva = true
    },

    cerrarDialogNueva () {
      this.dialogNueva = false
      this.resetFormNueva()
    },

    resetFormNueva () {
      this.archivoComprobante = null
      this.ocrResultado = null
      this.procesandoOcr = false
      this.alumnosEscuela = []
      this.gruposCiclo = []
      this.formSolicitud = {
        fecha: new Date().toISOString().slice(0, 10),
        motivo: '',
        id_alumno: null,
        id_grupo: null,
        id_ciclo: null,
      }
    },

    onArchivoElegido () {
      if (!this.archivoComprobante) {
        this.ocrResultado = null
        return
      }
      this.enviarOcr(this.archivoComprobante)
    },

    enviarOcr (file) {
      this.procesandoOcr = true
      this.ocrResultado = null

      const formData = new FormData()
      formData.append('file', file)
      formData.append('newVersion', '1')
      formData.append('noPagos', '0')

      return this.$http
        .post('lectura.imagenes', formData)
        .then((res) => {
          const data = res.data
          if (!data.ok) {
            this.validarErrorDirecto('No se pudo leer el comprobante.')
            return
          }
          this.ocrResultado = data

          let fechaSugerida = this.formSolicitud.fecha
          if (data.fechas && data.fechas.length) {
            const conFecha = data.fechas.find((f) => f.date) || data.fechas[0]
            if (conFecha && conFecha.date) fechaSugerida = conFecha.date.slice(0, 10)
          }
          this.formSolicitud.fecha = fechaSugerida
          this.$nextTick(() => {
            this.cargarAlumnosEscuela()
            if (this.formSolicitud.id_ciclo) {
              this.cargarGruposPorCiclo(this.formSolicitud.id_ciclo)
            }
          })
        })
        .catch((error) => {
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'Error al procesar la imagen'
          this.validarError(msg)
        })
        .finally(() => {
          this.procesandoOcr = false
        })
    },

    esPdfItem (item) {
      if (!item) return false
      if (item.es_pdf === true || item.es_pdf === 1 || item.es_pdf === '1') return true
      const u = String(item.url_comprobante || item.archivo_url || '')
      return u ? /\.pdf($|\?)/i.test(u) : false
    },

    revocarComprobanteBlob () {
      if (this.comprobanteBlobUrl) {
        URL.revokeObjectURL(this.comprobanteBlobUrl)
        this.comprobanteBlobUrl = null
      }
      this.comprobanteVistaEsPdf = false
    },

    async parseBlobErrorMessage (blob) {
      if (!(blob instanceof Blob)) return null
      const t = await new Response(blob).text()
      try {
        const j = JSON.parse(t)
        return j.message || t
      } catch {
        return t || null
      }
    },

    cargarComprobanteArchivo (idSolicitud) {
      this.revocarComprobanteBlob()
      this.errorComprobanteBlob = null
      this.cargandoComprobanteBlob = true

      return this.$http
        .get(`solicitud.comprobante.archivo/${idSolicitud}`, { responseType: 'blob' })
        .then((res) => {
          const blob = res.data
          if (!blob || blob.size === 0) {
            this.errorComprobanteBlob = 'El archivo está vacío o no disponible.'
            return
          }
          const tipo = blob.type || ''
          this.comprobanteVistaEsPdf =
            tipo === 'application/pdf' ||
            (this.detalleItem && this.esPdfItem(this.detalleItem))
          this.comprobanteBlobUrl = URL.createObjectURL(blob)
        })
        .catch(async (error) => {
          this.comprobanteBlobUrl = null
          let msg = 'No se pudo cargar el comprobante'
          if (error.response) {
            const st = error.response.status
            const d = error.response.data
            if (d instanceof Blob) {
              const parsed = await this.parseBlobErrorMessage(d)
              if (parsed) msg = parsed
              else if (st === 401 || st === 403) msg = 'Sin permiso para ver el comprobante'
              else if (st >= 500) msg = 'Error del servidor al obtener el archivo'
            } else if (d && typeof d === 'object' && d.message) {
              msg = d.message
            }
          }
          this.errorComprobanteBlob = msg
        })
        .finally(() => {
          this.cargandoComprobanteBlob = false
        })
    },

    abrirComprobanteBlobEnPestana () {
      if (!this.comprobanteBlobUrl) return
      window.open(this.comprobanteBlobUrl, '_blank', 'noopener,noreferrer')
    },

    guardarSolicitud () {
      if (!this.puedeGuardarSolicitud) return
      if (!this.archivoComprobante) {
        this.validarErrorDirecto('Falta el archivo del comprobante.')
        return
      }

      const fd = new FormData()
      fd.append('comprobante', this.archivoComprobante)
      fd.append('texto_normalizado', String(this.ocrResultado.textoNormalizado || '').trim())
      fd.append('id_usuario', String(this.idUsuarioActual))
      fd.append('fecha', this.formSolicitud.fecha)
      fd.append('motivo', String(this.formSolicitud.motivo).trim())
      fd.append('id_alumno', String(Number(this.formSolicitud.id_alumno)))
      fd.append('id_grupo', String(Number(this.formSolicitud.id_grupo)))
      fd.append('id_ciclo', String(Number(this.formSolicitud.id_ciclo)))
      fd.append('image_id', this.ocrResultado.image_id != null ? String(this.ocrResultado.image_id) : '')
      fd.append('es_pdf', this.ocrResultado.esPDF ? '1' : '0')

      this.guardandoSolicitud = true
      const auth = localStorage.tlaKey ? { Authorization: 'Bearer ' + localStorage.tlaKey } : {}
      return this.$http
        .post('solicitud.comprobante', fd, { headers: auth })
        .then((res) => {
          this.validarSuccess(res.data.message || 'Solicitud registrada')
          this.cerrarDialogNueva()
          this.cargarSolicitudes()
        })
        .catch((error) => {
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'No se pudo guardar. Crea la ruta POST solicitud.comprobante en el API.'
          this.validarError(msg)
        })
        .finally(() => {
          this.guardandoSolicitud = false
        })
    },

    aceptar (item) {
      if (!this.esRevisor) return
      if (!window.confirm('¿Aceptar esta solicitud?')) return

      this.procesandoAccion = true
      return this.$http
        .post('solicitud.comprobante.estatus', {
          id: item.id,
          estatus: 1,
          id_revisor: this.idUsuarioActual,
          texto_normalizado: String(item.texto_normalizado || ''),
        })
        .then((res) => {
          this.validarSuccess(res.data.message || 'Solicitud aceptada')
          this.cargarSolicitudes()
        })
        .catch((error) => {
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'Error al aceptar'
          this.validarError(msg)
        })
        .finally(() => {
          this.procesandoAccion = false
        })
    },

    abrirRechazo (item) {
      this.itemRechazo = item
      this.comentarioRechazo = ''
      this.dialogRechazo = true
    },

    confirmarRechazo () {
      if (!this.itemRechazo || !this.comentarioRechazo.trim()) return

      this.procesandoAccion = true
      return this.$http
        .post('solicitud.comprobante.estatus', {
          id: this.itemRechazo.id,
          estatus: 2,
          comentario_rechazo: this.comentarioRechazo.trim(),
          id_revisor: this.idUsuarioActual,
        })
        .then((res) => {
          this.validarSuccess(res.data.message || 'Solicitud rechazada')
          this.dialogRechazo = false
          this.itemRechazo = null
          this.cargarSolicitudes()
        })
        .catch((error) => {
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'Error al rechazar'
          this.validarError(msg)
        })
        .finally(() => {
          this.procesandoAccion = false
        })
    },

    verDetalle (item) {
      this.revocarComprobanteBlob()
      this.errorComprobanteBlob = null
      this.detalleItem = { ...item }
      this.dialogDetalle = true
      if (item && item.id) {
        this.cargandoComprobanteBlob = true
        this.$nextTick(() => {
          this.cargarComprobanteArchivo(item.id)
        })
      } else {
        this.errorComprobanteBlob = 'Esta solicitud no tiene id para cargar el comprobante.'
        this.cargandoComprobanteBlob = false
      }
    },

    exportarTabla () {
      const rows = this.filasTabla.map((r) => ({
        id: r.id,
        solicito: r.solicito != null && r.solicito !== '' ? `Solicitó: ${r.solicito}` : 'Solicitó: —',
        fecha: r.fecha,
        id_ciclo: r.id_ciclo,
        id_alumno: r.id_alumno,
        id_grupo: r.id_grupo,
        motivo: r.motivo,
        estatus: this.etiquetaEstatus(r.estatus),
      }))
      if (!rows.length) {
        this.validarErrorDirecto('No hay datos para exportar')
        return
      }
      this.exportExcel(rows, 'solicitudes_comprobante')
    },
  },
}
</script>
